import { observer } from 'mobx-react';
import * as React from 'react';
import {
  AreaInput,
  Button,
  Card,
  DateInput,
  FormCell,
  Icon,
  IconSize,
  IconType,
  NumberInput,
  Select,
  TextInput,
  Grid,
  FlexRow,
} from 'ui-toolkit';
import { mortgageDocumentOptions } from '../../documentTypes';
import { ISearchReportChange } from '../../services/SearchReportService';
import { MortgageDocument } from './MortgageDocument';
import { IMortgageData } from '../../../ui-toolkit/orders/interfaces/titleReport/IMortgage';

// tslint:disable:jsx-no-lambda TODO: remove this tslint disable

interface IProps {
  data: IMortgageData[];
  error?: any;
  title?: string;
  onFormChange: (dataPath: string, newData: ISearchReportChange, error?: string | null | undefined) => void;
  onAddNewItem: (dataPath: string) => void;
  onDeleteItem: (dataPath: string, index?: number) => void;
  validateFormData: (path: string) => void;
}

export const MortgageInformation = observer(
  class MortgageInformation extends React.Component<IProps, {}> {
    public static defaultProps: Partial<IProps> = {
      title: 'Mortgage Information',
    };

    public render() {
      const dataLen = this.props.data.length;

      return (
        <Card title={this.props.title}>
          <Grid>
            {this.props.data.map((mortgage: IMortgageData, index: number) => {
              return (
                <React.Fragment key={`mortgage-information-${index}`}>
                  <FlexRow justifyContent={'space-between'}>
                    <h3>Mortgage No {index + 1}</h3>
                    {this.props.data.length > 1 ? (
                      <Button
                        intent="ghost"
                        isInline
                        onClick={(e: any) => this.props.onDeleteItem('mortgageInformation', index)}
                      >
                        <Icon type={IconType.Remove} size={IconSize.Small} />
                      </Button>
                    ) : null}
                  </FlexRow>

                  <Grid columnCount={3}>
                    <TextInput
                      value={mortgage.mortgagorName}
                      label="Mortgagor name"
                      placeholder="Ex. Samuel Jackson"
                      onChange={(value, error) => this.onChange('mortgagorName', index, value)}
                    />
                    <TextInput
                      value={mortgage.lenderName}
                      label="Lender name"
                      placeholder="Ex. Central Loans"
                      onChange={(value, error) => this.onChange(`lenderName`, index, value)}
                    />
                    <TextInput
                      value={mortgage.trusteeName}
                      label="Trustee name"
                      placeholder="Ex. Jean Jackson"
                      onChange={(value, error) => this.onChange(`trusteeName`, index, value)}
                    />
                  </Grid>
                  <Grid columnCount={2}>
                    <Grid columnCount={this.props.data[index].documentType === 'other' ? 2 : 1}>
                      <FormCell
                        errorText={this.props.error[`mortgageInformation[${index}].documentType`]}
                        className={
                          this.props.data[index].documentType === 'other'
                            ? 'card--row-element '
                            : 'card--row-element half-row'
                        }
                        label="Document type"
                      >
                        <Select
                          isClearable={true}
                          value={mortgageDocumentOptions.filter((opt) => opt.value === mortgage.documentType)}
                          placeholder="Select"
                          options={mortgageDocumentOptions}
                          onChange={(value: any) => this.onDocumentTypeChange(index, value && value.value, null)}
                        />
                      </FormCell>
                      {this.props.data[index].documentType === 'other' ? (
                        <TextInput
                          value={mortgage.otherDocumentType}
                          errorText={this.props.error[`mortgageInformation[${index}].otherDocumentType`]}
                          label="Other document type"
                          isRequired={this.props.data[index].documentType === 'other'}
                          placeholder="Add document type name"
                          className="card--row-element"
                          onChange={(value, error) => this.onChange('otherDocumentType', index, value, error)}
                          onBlur={() => this.props.validateFormData(`mortgageInformation[${index}].otherDocumentType`)}
                        />
                      ) : null}
                    </Grid>
                    <Grid columnCount={2}>
                      <DateInput
                        value={mortgage.originalDate || null}
                        errorText={this.props.error[`mortgageInformation[${index}].originalDate`]}
                        label="Mortgage Date"
                        className="card--row-element"
                        onChange={(value) => this.onChange(`originalDate`, index, value.value)}
                      />
                      <DateInput
                        value={mortgage.recordedDate || null}
                        errorText={this.props.error[`mortgageInformation[${index}].recordedDate`]}
                        label="Recorded date"
                        className="card--row-element"
                        onChange={(value) => this.onChange(`recordedDate`, index, value.value)}
                      />
                    </Grid>
                  </Grid>
                  <Grid columnCount={2}>
                    <NumberInput
                      value={mortgage.originalLoanAmount}
                      label="Original Loan Amount / credit limit"
                      prefix="$"
                      placeholder="XXX,XXX,XXX.XX"
                      decimalScale={2}
                      onChange={(value, error) => this.onChange(`originalLoanAmount`, index, value, error)}
                      mode="currency"
                      thousandSeparator={true}
                    />
                    <DateInput
                      value={mortgage.maturityDate || null}
                      errorText={this.props.error[`mortgageInformation[${index}].maturityDate`]}
                      label="Maturity Date"
                      onChange={(value) => this.onChange(`maturityDate`, index, value.value)}
                    />
                  </Grid>
                  <Grid columnCount={2}>
                    <Grid columnCount={2}>
                      <TextInput
                        value={mortgage.instrumentNumber}
                        label="Instrument No."
                        placeholder="XXXXXXXXXX"
                        onChange={(value, error) => this.onChange(`instrumentNumber`, index, value, error)}
                      />
                      <TextInput
                        value={mortgage.book}
                        label="Book/page"
                        placeholder="XXXX"
                        onChange={(value, error) => this.onChange(`book`, index, value, error)}
                      />
                    </Grid>
                    <AreaInput
                      value={mortgage.additionalInformation}
                      label="Additional information"
                      rows={1}
                      placeholder="Please include additional information here"
                      onChange={(value, error) => this.onChange(`additionalInformation`, index, value, error)}
                    />
                  </Grid>
                  {Array.isArray(this.props.data[index].documents) && this.props.data[index].documents!!.length ? (
                    <MortgageDocument
                      data={this.props.data[index].documents!!}
                      error={this.props.error}
                      dataPath={`mortgageInformation[${index}].documents`}
                      onFormChange={this.props.onFormChange}
                      onAddNewItem={this.props.onAddNewItem}
                      onDeleteItem={this.props.onDeleteItem}
                      validateFormData={this.props.validateFormData}
                    />
                  ) : null}
                  <div>
                    <Button
                      intent="ghost"
                      isInline
                      onClick={(e) => this.props.onAddNewItem(`mortgageInformation[${index}].documents`)}
                    >
                      + Add Document
                    </Button>
                  </div>
                  {index !== dataLen - 1 && <hr className="card-horizontal-separator" />}
                </React.Fragment>
              );
            })}
            <FlexRow>
              <Button intent="secondary" onClick={(e) => this.props.onAddNewItem('mortgageInformation')}>
                Another mortgage
              </Button>
            </FlexRow>
          </Grid>
        </Card>
      );
    }

    private onDocumentTypeChange = (index: number, value: string, error: string | null | undefined) => {
      this.onChange(`documentType`, index, value, error);
      if (value !== 'other') {
        this.onChange(`otherDocumentType`, index, null, null);
      }
    };

    private onChange = (itemDataPath: string, itemIndex: number, newValue: any, error?: string | null | undefined) => {
      this.props.onFormChange(
        'mortgageInformation',
        {
          data: newValue,
          dataPath: itemDataPath,
          index: itemIndex,
        },
        error
      );
    };
  }
);
